"use strict";

var _interopRequireWildcard = require("@babel/runtime/helpers/interopRequireWildcard").default;
var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault").default;
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.TabList = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _styled = _interopRequireDefault(require("@emotion/styled"));
var _lodestarUtils = require("@smartsheet/lodestar-utils");
var _react = _interopRequireWildcard(require("react"));
var _useTheme = require("../../theme/utils/useTheme");
var _TabUnderline = require("./TabUnderline");
var _TabsContext = require("./TabsContext");
var _findIndex = require("./findIndex");
var _tabsTheme = require("./tabsTheme");
const TabsContainer = (0, _styled.default)("div")`
    display: inline-flex;
    height: ${_ref => {
  let {
    themeMode
  } = _ref;
  return _tabsTheme.tabsTheme.containerHeight(themeMode);
}};
    // Adding relative to allow absolutely-positioned Underlines.
    // This also creates a stacking context for the tabs/underline without using a z-index.
    position: relative;
    isolation: isolate;
    white-space: nowrap;
`;
const TabList = /*#__PURE__*/(0, _react.forwardRef)((_ref2, providedRef) => {
  let {
    children,
    components = {},
    ...rest
  } = _ref2;
  const localRef = (0, _react.useRef)(null);
  const ref = (0, _lodestarUtils.useForkRef)(localRef, providedRef);
  const {
    selectedId,
    renderOn,
    tabRegistrations
  } = (0, _TabsContext.useTabsContext)();
  const {
    key
  } = (0, _useTheme.useTheme)();
  const themeMode = key;
  const underlineShouldTransitionRef = (0, _react.useRef)(false);
  const showTransitions = () => {
    // We only want to start transitioning on the 1st hover or selection.
    // We don't want to transtition on mount or the bar grows from 0
    // to whatever the selected item's width is.
    // Use a ref since changing this shouldn't cause a re-render by itself,
    // but it will be used in subsequent renders.
    underlineShouldTransitionRef.current = true;
  };
  const tabRefs = (0, _react.useMemo)(() => tabRegistrations.map(_ref3 => {
    let {
      tabRef
    } = _ref3;
    return tabRef.current;
  }).filter(Boolean), [tabRegistrations]);
  const selectedIndex = (0, _findIndex.findIndex)(tabRefs, tabRef => tabRef.id === selectedId);
  const selectedSliderCoords = useUnderline(tabRefs[selectedIndex]);
  const [hoveredSliderCoords, setHoveredSliderCoords] = (0, _react.useState)({
    left: 0,
    width: 0
  });
  const setHoveredSliderCoordsCb = (0, _react.useCallback)(hoveredIndex => {
    const hoveredTabRef = tabRefs[hoveredIndex];
    if (hoveredTabRef) {
      const left = hoveredTabRef.offsetLeft;
      const width = hoveredTabRef.offsetWidth;
      setHoveredSliderCoords({
        left,
        width
      });
    }
  }, [tabRefs]);
  (0, _react.useEffect)(() => {
    setHoveredSliderCoordsCb(selectedIndex);
  }, [selectedIndex, setHoveredSliderCoordsCb, themeMode]);

  // Used to hide the hover slider for a tick to re-mount it back at the selected
  const [showHoverUnderline, setShowHoverUnderline] = (0, _react.useState)(true);
  const moveHoverSliderBackToSelectedTab = () => {
    setShowHoverUnderline(false);
    setHoveredSliderCoordsCb(selectedIndex);
    setTimeout(() => {
      setShowHoverUnderline(true);
    }, 0);
  };
  const Underline = components.TabUnderline || _TabUnderline.TabUnderline;
  if (!Underline.displayName) {
    Underline.displayName = _TabUnderline.TabUnderline.displayName;
  }
  return /*#__PURE__*/_react.default.createElement(TabsContainer, (0, _extends2.default)({}, rest, {
    ref: ref,
    role: "tablist",
    themeMode: themeMode
    // Note: onFocus bubbles in React, not in the DOM spec.
    // We don't care about focusing THIS element, but instead care about the
    // children receiving focus to start animations.
    ,
    onFocus: (0, _lodestarUtils.callAllHandlers)(() => {
      showTransitions();
    }, rest.onFocus),
    onMouseEnter: (0, _lodestarUtils.callAllHandlers)(() => {
      showTransitions();
    }, rest.onMouseEnter),
    onMouseLeave: (0, _lodestarUtils.callAllHandlers)(() => {
      moveHoverSliderBackToSelectedTab();
    }, rest.onMouseLeave)
  }), _react.default.Children.map(children, (child, childIndex) => {
    if (! /*#__PURE__*/_react.default.isValidElement(child)) {
      return child;
    }
    const props = child.props;
    return /*#__PURE__*/_react.default.cloneElement(child, {
      onMouseEnter: (0, _lodestarUtils.callAllHandlers)(() => {
        setHoveredSliderCoordsCb(childIndex);
      }, props.onMouseEnter),
      onClick: (0, _lodestarUtils.callAllHandlers)(() => {
        if (childIndex === selectedIndex) {
          return;
        }
        showTransitions();
      }, props.onClick)
    });
  }), /*#__PURE__*/_react.default.createElement(Underline, {
    leftPx: 0,
    widthPlusUnit: "100%",
    backgroundColor: _tabsTheme.tabsTheme[renderOn].underlineColor(themeMode),
    height: _tabsTheme.tabsTheme.underlineHeight.default(themeMode)
  }), showHoverUnderline && /*#__PURE__*/_react.default.createElement(Underline, {
    leftPx: hoveredSliderCoords.left,
    widthPlusUnit: hoveredSliderCoords.width + "px",
    backgroundColor: _tabsTheme.tabsTheme[renderOn].underlineHoverColor(themeMode),
    underlineTransition: underlineShouldTransitionRef.current,
    height: _tabsTheme.tabsTheme.underlineHeight.active(themeMode)
  }), /*#__PURE__*/_react.default.createElement(Underline, {
    leftPx: selectedSliderCoords.left,
    widthPlusUnit: selectedSliderCoords.width + "px",
    backgroundColor: _tabsTheme.tabsTheme.underlineSelectedColor(themeMode),
    underlineTransition: underlineShouldTransitionRef.current,
    height: _tabsTheme.tabsTheme.underlineHeight.active(themeMode)
  }));
});
exports.TabList = TabList;
TabList.displayName = "TabList";
const useUnderline = tabRef => {
  const [sliderCoords, setSliderCoords] = (0, _react.useState)({
    left: 0,
    width: 0
  });
  (0, _react.useEffect)(() => {
    if (tabRef) {
      const left = tabRef.offsetLeft;
      const width = tabRef.offsetWidth;
      setSliderCoords({
        left,
        width
      });
    }
  }, [tabRef]);
  return sliderCoords;
};
//# sourceMappingURL=TabList.js.map